import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';

@Component({
    selector: 'app-process-cancellation',
    templateUrl: './process-cancellation.component.html',
    styleUrls: ['./process-cancellation.component.scss'],
    providers: [DecimalPipe, CurrencyPipe, DatePipe],
    standalone: false
})
export class ProcessCancellationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  contractDetails = {
    contractNumber: 'CT-2024-030',
    chassi: '9BZF55POXB567890',
    planName: 'Ford Protect Premium',
    finalPrice: 6800.00,
    fordDebit: -1088.80,
    saleDate: new Date('2024-05-19'),
    contractStartDate: new Date('2024-05-19')
  };

  cancellationForm = {
    cancellationDate: new Date(),
    totalMonths: 48,
    consumedMonths: 18.75, // Exemplo com decimal
    balanceMonths: 30,
    initialBalanceMonths: 30,
    initialResidualValueMonths: 4217.42,
    residualValueMonths: 4217.42,

    totalVisits: 8,
    consumedVisits: 2,
    balanceVisits: 6,
    initialBalanceVisits: 6,
    initialResidualValueVisits: 5100.00,
    residualValueVisits: 5100.00,

    totalMileage: 80000,
    consumedMileage: 30383,
    balanceMileage: 49617,
    initialBalanceMileage: 49617,
    initialResidualValueMileage: 4217.45,
    residualValueMileage: 4217.45,

    finalResidualValue: 0, // Este valor será definido no ngOnInit ou por cálculo/usuário
    cancellationReason: 'Insatisfação com Serviço',
    additionalObservations: 'O cliente expressou insatisfação com o atendimento recebido na última visita.',
    dealerEmail: 'contato@concessionaria.com.br',
    sellerEmail: 'vendedor@concessionaria.com.br',
    ipcaPercentage: 80.00,
    administrativeTax: 10.00,
  };

  calculatedValues = {
    ipcaCorrection: 0,
    customerReturn: 0,
    fordReturn: 0,
    dealerReturn: 0,
    isInvalidReturn: false
  };

  // --- Variáveis de estado de validação manual ---
  userWithPermission = false;

  formSubmitted = false;

  consumedMonthsError: string | null = null;
  consumedMonthsTouched = false;

  consumedVisitsError: string | null = null;
  consumedVisitsTouched = false;

  consumedMileageError: string | null = null;
  consumedMileageTouched = false;

  finalResidualValueError: string | null = null;
  finalResidualValueTouched = false;

  additionalObservationsError: string | null = null;
  additionalObservationsTouched = false;

  dealerEmailError: string | null = null;
  dealerEmailTouched = false;

  sellerEmailError: string | null = null;
  sellerEmailTouched = false;

  ipcaPercentageError: string | null = null;
  ipcaPercentageTouched = false;

  administrativeTaxError: string | null = null;
  administrativeTaxTouched = false;

  // --- PROPRIEDADES PARA VALORES DE EXIBIÇÃO FORMATADOS ---
  _consumedMonthsDisplay = '';
  _consumedVisitsDisplay = '';
  _consumedMileageDisplay = '';
  _finalResidualValueDisplay = '';
  _ipcaPercentageDisplay = '';
  _administrativeTaxDisplay = '';

  // --- NOVO FLAG PARA VALOR RESIDUAL FINAL MANUAL ---
  isFinalResidualValueManuallySet = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private decimalPipe: DecimalPipe,
    private currencyPipe: CurrencyPipe,
    private messageService: MessageService,
    private apsPermissionService: ApsPermissionService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {

    // this.userWithPermission = this.apsPermissionService.permissionValidation('TMAC:ScreenProcessContractCancel');

    // if (!this.userWithPermission) {

    //   this.messageService.add({
    //     severity: 'error',
    //     summary: this.translateService.instant('TRANSLATE.ERROR'),
    //     detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION')
    //   });

    //   setTimeout(() => {
    //     this.router.navigate(['/home']); 
    //   }, 500);

    //   return; 
    // }

    this.route.paramMap.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      const contractNumber = params.get('contractNumber');
      if (contractNumber) {
        // ...
      }
    });

    // Inicia os cálculos e define o valor residual final inicial
    this.onConsumptionOrMileageChange();
    this.calculateSummaryValues();

    // Inicializa as propriedades de exibição com os valores formatados APÓS os cálculos
    this.initializeDisplayValues();

  }

  private initializeDisplayValues(): void {
    this._consumedMonthsDisplay = this.decimalPipe.transform(this.cancellationForm.consumedMonths, '1.2-2', 'pt-BR') || '';
    this._consumedVisitsDisplay = this.decimalPipe.transform(this.cancellationForm.consumedVisits, '1.0-0', 'pt-BR') || '';
    this._consumedMileageDisplay = this.decimalPipe.transform(this.cancellationForm.consumedMileage, '1.0-0', 'pt-BR') || '';
    this._finalResidualValueDisplay = this.currencyPipe.transform(this.cancellationForm.finalResidualValue, 'BRL', 'symbol', '1.2-2', 'pt-BR') || '';
    this._ipcaPercentageDisplay = this.decimalPipe.transform(this.cancellationForm.ipcaPercentage, '1.2-2', 'pt-BR') || '';
    this._administrativeTaxDisplay = this.currencyPipe.transform(this.cancellationForm.administrativeTax, 'BRL', 'symbol', '1.2-2', 'pt-BR') || '';
  }

  // --- Funções Auxiliares de Formatação e Parsing ---

  private parseFormattedValue(value: string): number {
    if (!value) return 0;
    const cleanedValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanedValue) || 0;
  }

  private formatToCurrencyDisplay(value: string): string {
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2);
      value = parts.join(',');
    }
    return value;
  }

  private formatToDecimalDisplay(value: string): string {
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2);
      value = parts.join(',');
    }
    return value;
  }

  private formatToMileageDisplay(value: string): string {
    value = value.replace(/[^0-9]/g, '');
    if (value.length === 0) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // --- Métodos de Validação para cada campo ---
  validateConsumedMonths(): void {
    const value = this.cancellationForm.consumedMonths;
    if (isNaN(value)) {
      this.consumedMonthsError = this.translateService.instant('TRANSLATE.INVALID.DECIMAL');
    } else if (value < 0) {
      this.consumedMonthsError = this.translateService.instant('TRANSLATE.CONSUMED.MONTHS.NEGATIVE');
    } else if (value > this.cancellationForm.totalMonths) {
      this.consumedMonthsError = `${this.translateService.instant('TRANSLATE.CONSUMED.MONTHS.EXCEEDS')} (${this.cancellationForm.totalMonths}).`;
    } else {
      this.consumedMonthsError = null;
    }
  }

  validateConsumedVisits(): void {
    const value = this.cancellationForm.consumedVisits;
    if (isNaN(value)) {
      this.consumedVisitsError = this.translateService.instant('TRANSLATE.INVALID.INTEGER');
    } else if (value < 0) {
      this.consumedVisitsError = this.translateService.instant('TRANSLATE.CONSUMED.VISITS.NEGATIVE');
    } else if (value > this.cancellationForm.totalVisits) {
      this.consumedVisitsError = `${this.translateService.instant('TRANSLATE.CONSUMED.VISITS.EXCEEDS')} (${this.cancellationForm.totalVisits}).`;
    } else {
      this.consumedVisitsError = null;
    }
  }

  validateConsumedMileage(): void {
    const value = this.cancellationForm.consumedMileage;
    if (isNaN(value)) {
      this.consumedMileageError = this.translateService.instant('TRANSLATE.INVALID.INTEGER');
    } else if (value < 0) {
      this.consumedMileageError = this.translateService.instant('TRANSLATE.CONSUMED.MILEAGE.NEGATIVE');
    } else if (value > this.cancellationForm.totalMileage) {
      this.consumedMileageError = `${this.translateService.instant('TRANSLATE.CONSUMED.MILEAGE.EXCEEDS')} (${this.cancellationForm.totalMileage} km).`;
    } else {
      this.consumedMileageError = null;
    }
  }

  validateFinalResidualValue(): void {
    const value = this.cancellationForm.finalResidualValue;
    if (isNaN(value)) {
      this.finalResidualValueError = this.translateService.instant('TRANSLATE.INVALID.CURRENCY');
    } else if (value < 0) {
      this.finalResidualValueError = this.translateService.instant('TRANSLATE.RESIDUAL.FINAL.VALUE.NEGATIVE');
    } else {
      this.finalResidualValueError = null;
    }
  }

  validateAdditionalObservations(): void {
    const value = this.cancellationForm.additionalObservations;
    if (value === null || value === undefined || value.length === 0) {
      this.additionalObservationsError = this.translateService.instant('TRANSLATE.ADDITIONAL.REMARKS.REQUIRED');
    } else if (value.length < 2) {
      this.additionalObservationsError = this.translateService.instant('TRANSLATE.ADDITIONAL.MIN.LENGTH');
    } else if (value.length > 500) {
      this.additionalObservationsError = this.translateService.instant('TRANSLATE.ADDITIONAL.REMARKS.MAX.LENGTH');
    } else {
      this.additionalObservationsError = null;
    }
  }

  validateDealerEmail(): void {
    const value = this.cancellationForm.dealerEmail;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value === null || value === undefined || value.length === 0) {
      this.dealerEmailError = this.translateService.instant('TRANSLATE.DEALER.EMAIL.REQUIRED');
    } else if (value.length < 2) {
      this.dealerEmailError = this.translateService.instant('TRANSLATE.ADDITIONAL.MIN.LENGTH');
    } else if (value.length > 80) {
      this.dealerEmailError = this.translateService.instant('TRANSLATE.ADDITIONAL.MAX.LENGTH');
    } else if (!emailRegex.test(value)) {
      this.dealerEmailError = this.translateService.instant('TRANSLATE.INVALID.EMAIL');
    } else {
      this.dealerEmailError = null;
    }
  }

  validateSellerEmail(): void {
    const value = this.cancellationForm.sellerEmail;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value === null || value === undefined || value.length === 0) {
      this.sellerEmailError = this.translateService.instant('TRANSLATE.SELLER.EMAIL.REQUIRED');
    } else if (value.length < 2) {
      this.sellerEmailError = this.translateService.instant('TRANSLATE.ADDITIONAL.MIN.LENGTH');
    } else if (value.length > 80) {
      this.sellerEmailError = this.translateService.instant('TRANSLATE.ADDITIONAL.MAX.LENGTH');
    } else if (!emailRegex.test(value)) {
      this.sellerEmailError = this.translateService.instant('TRANSLATE.INVALID.EMAIL');
    } else {
      this.sellerEmailError = null;
    }
  }

  validateIpcaPercentage(): void {
    const value = this.cancellationForm.ipcaPercentage;
    if (isNaN(value)) {
      this.ipcaPercentageError = this.translateService.instant('TRANSLATE.INVALID.DECIMAL');
    } else if (value < 0) {
      this.ipcaPercentageError = this.translateService.instant('TRANSLATE.IPCA.PERCENTAGE.NEGATIVE');
    } else {
      this.ipcaPercentageError = null;
    }
  }

  validateAdministrativeTax(): void {
    const value = this.cancellationForm.administrativeTax;
    if (isNaN(value)) {
      this.administrativeTaxError = this.translateService.instant('TRANSLATE.INVALID.CURRENCY.MONEY');
    } else if (value < 0) {
      this.administrativeTaxError = this.translateService.instant('TRANSLATE.ADMINISTRATIVE.TAX.NEGATIVE');
    } else {
      this.administrativeTaxError = null;
    }
  }

  // --- Global checker para saber se o formulário está válido ---
  isFormValid(): boolean {
    return !this.consumedMonthsError &&
      !this.consumedVisitsError &&
      !this.consumedMileageError &&
      !this.finalResidualValueError &&
      !this.additionalObservationsError &&
      !this.dealerEmailError &&
      !this.sellerEmailError &&
      !this.ipcaPercentageError &&
      !this.administrativeTaxError;
  }

  // --- Funções de Manipulação de Input e Blur ---

  onInputMonths(event: Event): void {
    // Redefine o flag de alteração manual para forçar o recálculo do Valor Residual Final
    this.isFinalResidualValueManuallySet = false; // <--- ADICIONADO AQUI

    const input = event.target as HTMLInputElement;
    const originalCursorPosition = input.selectionStart ?? 0;
    const originalValue = input.value;

    const formattedValue = this.formatToDecimalDisplay(originalValue);
    this._consumedMonthsDisplay = formattedValue;
    this.cancellationForm.consumedMonths = this.parseFormattedValue(formattedValue);
    this.onConsumptionOrMileageChange(); // Isso vai chamar calculateFinalResidualValue()
    this.validateConsumedMonths();

    const newCursorPosition = originalCursorPosition + (formattedValue.length - originalValue.length);
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }

  onBlurMonths(): void {
    this.consumedMonthsTouched = true;
    this.validateConsumedMonths();
    if (!this.consumedMonthsError) {
      this._consumedMonthsDisplay = this.decimalPipe.transform(this.cancellationForm.consumedMonths, '1.2-2', 'pt-BR') || '';
    }
  }

  onInputVisits(event: Event): void {
    // Redefine o flag de alteração manual para forçar o recálculo do Valor Residual Final
    this.isFinalResidualValueManuallySet = false; // <--- ADICIONADO AQUI

    const input = event.target as HTMLInputElement;
    const cleanedValue = input.value.replace(/[^0-9]/g, '');
    this._consumedVisitsDisplay = cleanedValue;
    this.cancellationForm.consumedVisits = parseInt(cleanedValue, 10) || 0;
    this.onConsumptionOrMileageChange(); // Isso vai chamar calculateFinalResidualValue()
    this.validateConsumedVisits();
  }
  onBlurVisits(): void {
    this.consumedVisitsTouched = true;
    this.validateConsumedVisits();
    if (!this.consumedVisitsError) {
      this._consumedVisitsDisplay = this.decimalPipe.transform(this.cancellationForm.consumedVisits, '1.0-0', 'pt-BR') || '';
    }
  }

  onInputMileage(event: Event): void {
    // Redefine o flag de alteração manual para forçar o recálculo do Valor Residual Final
    this.isFinalResidualValueManuallySet = false; // <--- ADICIONADO AQUI

    const input = event.target as HTMLInputElement;
    const originalCursorPosition = input.selectionStart ?? 0;
    const originalValue = input.value;

    const cleanedValue = originalValue.replace(/[^0-9]/g, '');
    const formattedValue = this.formatToMileageDisplay(cleanedValue);

    this._consumedMileageDisplay = formattedValue;
    this.cancellationForm.consumedMileage = this.parseFormattedValue(formattedValue);
    this.onConsumptionOrMileageChange(); // Isso vai chamar calculateFinalResidualValue()
    this.validateConsumedMileage();

    const newCursorPosition = originalCursorPosition + (formattedValue.length - originalValue.length);
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }
  onBlurMileage(): void {
    this.consumedMileageTouched = true;
    this.validateConsumedMileage();
    if (!this.consumedMileageError) {
      this._consumedMileageDisplay = this.decimalPipe.transform(this.cancellationForm.consumedMileage, '1.0-0', 'pt-BR') || '';
    }
  }

  onInputIpcaPercentage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const originalCursorPosition = input.selectionStart ?? 0;
    const originalValue = input.value;

    const formattedValue = this.formatToDecimalDisplay(originalValue);
    this._ipcaPercentageDisplay = formattedValue;
    this.cancellationForm.ipcaPercentage = this.parseFormattedValue(formattedValue);
    this.calculateSummaryValues();
    this.validateIpcaPercentage();

    const newCursorPosition = originalCursorPosition + (formattedValue.length - originalValue.length);
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }
  onBlurIpcaPercentage(): void {
    this.ipcaPercentageTouched = true;
    this.validateIpcaPercentage();
    if (!this.ipcaPercentageError) {
      this._ipcaPercentageDisplay = this.decimalPipe.transform(this.cancellationForm.ipcaPercentage, '1.2-2', 'pt-BR') || '';
    }
  }

  onInputAdministrativeTax(event: Event): void {
    const input = event.target as HTMLInputElement;
    const originalCursorPosition = input.selectionStart ?? 0;
    const originalValue = input.value;

    const formattedValue = this.formatToCurrencyDisplay(originalValue);
    this._administrativeTaxDisplay = formattedValue;
    this.cancellationForm.administrativeTax = this.parseFormattedValue(formattedValue);
    this.calculateSummaryValues();
    this.validateAdministrativeTax();

    const newCursorPosition = originalCursorPosition + (formattedValue.length - originalValue.length);
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }
  onBlurAdministrativeTax(): void {
    this.administrativeTaxTouched = true;
    this.validateAdministrativeTax();
    if (!this.administrativeTaxError) {
      this._administrativeTaxDisplay = this.currencyPipe.transform(this.cancellationForm.administrativeTax, 'BRL', 'symbol', '1.2-2', 'pt-BR') || '';
    }
  }

  onInputFinalResidualValue(event: Event): void {
    const input = event.target as HTMLInputElement;
    const originalCursorPosition = input.selectionStart ?? 0;
    const originalValue = input.value;

    // MARCA QUE O VALOR FOI ALTERADO MANUALMENTE
    this.isFinalResidualValueManuallySet = true; // <--- MANTIDO

    const formattedValue = this.formatToCurrencyDisplay(originalValue);
    this._finalResidualValueDisplay = formattedValue;
    this.cancellationForm.finalResidualValue = this.parseFormattedValue(formattedValue);
    this.calculateSummaryValues(); // Recalcula os valores dependentes
    this.validateFinalResidualValue();

    const newCursorPosition = originalCursorPosition + (formattedValue.length - originalValue.length);
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }

  onBlurFinalResidualValue(): void {
    this.finalResidualValueTouched = true;
    this.validateFinalResidualValue();
    if (!this.finalResidualValueError) {
      this._finalResidualValueDisplay = this.currencyPipe.transform(this.cancellationForm.finalResidualValue, 'BRL', 'symbol', '1.2-2', 'pt-BR') || '';
    }
  }

  // Handlers para inputs que usam [(ngModel)] (não numéricos, sem formatação específica no blur)
  onAdditionalObservationsChange(value: string): void {
    this.cancellationForm.additionalObservations = value;
    this.validateAdditionalObservations();
  }
  onBlurAdditionalObservations(): void {
    this.additionalObservationsTouched = true;
    this.validateAdditionalObservations();
  }

  onDealerEmailChange(value: string): void {
    this.cancellationForm.dealerEmail = value;
    this.validateDealerEmail();
  }
  onBlurDealerEmail(): void {
    this.dealerEmailTouched = true;
    this.validateDealerEmail();
  }

  onSellerEmailChange(value: string): void {
    this.cancellationForm.sellerEmail = value;
    this.validateSellerEmail();
  }
  onBlurSellerEmail(): void {
    this.sellerEmailTouched = true;
    this.validateSellerEmail();
  }

  // --- Funções de Cálculo Existentes ---

  onConsumptionOrMileageChange(): void {
    this.cancellationForm.balanceMonths = this.cancellationForm.totalMonths - this.cancellationForm.consumedMonths;
    this.cancellationForm.balanceVisits = this.cancellationForm.totalVisits - this.cancellationForm.consumedVisits;
    this.cancellationForm.balanceMileage = this.cancellationForm.totalMileage - this.cancellationForm.consumedMileage;

    if (this.cancellationForm.initialBalanceMonths > 0) {
      this.cancellationForm.residualValueMonths =
        (this.cancellationForm.balanceMonths / this.cancellationForm.initialBalanceMonths) * this.cancellationForm.initialResidualValueMonths;
    } else {
      this.cancellationForm.residualValueMonths = 0;
    }
    this.cancellationForm.residualValueMonths = Math.max(0, this.cancellationForm.residualValueMonths);

    if (this.cancellationForm.initialBalanceVisits > 0) {
      this.cancellationForm.residualValueVisits =
        (this.cancellationForm.balanceVisits / this.cancellationForm.initialBalanceVisits) * this.cancellationForm.initialResidualValueVisits;
    } else {
      this.cancellationForm.residualValueVisits = 0;
    }
    this.cancellationForm.residualValueVisits = Math.max(0, this.cancellationForm.residualValueVisits);

    if (this.cancellationForm.initialBalanceMileage > 0) {
      this.cancellationForm.residualValueMileage =
        (this.cancellationForm.balanceMileage / this.cancellationForm.initialBalanceMileage) * this.cancellationForm.initialResidualValueMileage;
    } else {
      this.cancellationForm.residualValueMileage = 0;
    }
    this.cancellationForm.residualValueMileage = Math.max(0, this.cancellationForm.residualValueMileage);

    // CHAMA A FUNÇÃO DE CÁLCULO DO VALOR RESIDUAL FINAL
    this.calculateFinalResidualValue();
    this.calculateSummaryValues();
  }

  calculateFinalResidualValue(): void {
    // ESTA FUNÇÃO AGORA SEMPRE CALCULA O MENOR VALOR E ATRIBUI.
    // A LÓGICA DE SOBRESCREVER VALOR MANUALMENTE SÓ OCORRE SE isFinalResidualValueManuallySet for false.
    // Se o flag for true, o valor do usuário é mantido.
    const calculatedMinResidual = Math.min(
      this.cancellationForm.residualValueMonths,
      this.cancellationForm.residualValueVisits,
      this.cancellationForm.residualValueMileage
    );

    if (!this.isFinalResidualValueManuallySet) { // <--- CONDIÇÃO MANTIDA
      this.cancellationForm.finalResidualValue = calculatedMinResidual;
      // Atualiza a propriedade de exibição imediatamente para refletir o cálculo automático
      this._finalResidualValueDisplay = this.currencyPipe.transform(this.cancellationForm.finalResidualValue, 'BRL', 'symbol', '1.2-2', 'pt-BR') || '';
    }
    // Se isFinalResidualValueManuallySet for true, o valor do modelo e o de exibição
    // que foram definidos pelo usuário em onInputFinalResidualValue são mantidos.
    // O usuário pode então alterar novamente.
  }

  calculateSummaryValues(): void {
    this.calculatedValues.ipcaCorrection =
      this.cancellationForm.finalResidualValue * (this.cancellationForm.ipcaPercentage / 100);

    this.calculatedValues.customerReturn =
      this.cancellationForm.finalResidualValue +
      this.calculatedValues.ipcaCorrection -
      this.cancellationForm.administrativeTax;

    this.calculatedValues.fordReturn = this.calculatedValues.customerReturn * 0.80;
    this.calculatedValues.dealerReturn = this.calculatedValues.customerReturn * 0.20;

    this.calculatedValues.isInvalidReturn = this.calculatedValues.customerReturn > this.contractDetails.finalPrice;
  }

  goBack(): void {
    this.router.navigate(['/view-pending-cancellations']);
  }

  private focusFieldById(fieldId: string): void {
    console.log(`[FOCUS DEBUG] focusFieldById called with fieldId: "${fieldId}"`);
    
    // First check if element exists immediately
    const immediateElement = document.getElementById(fieldId);
    console.log(`[FOCUS DEBUG] Element found immediately:`, !!immediateElement);
    
    // Strategy: Use multiple timing approaches to ensure focus works
    // First attempt: immediate
    this.attemptFocus(fieldId, 0);
    // Second attempt: after microtask
    this.attemptFocus(fieldId, 10);
    // Third attempt: after paint
    this.attemptFocus(fieldId, 50);
    // Fourth attempt: with Angular zone awareness
    this.attemptFocus(fieldId, 100);
  }

  private attemptFocus(fieldId: string, delayMs: number): void {
    setTimeout(() => {
      const element = document.getElementById(fieldId) as (HTMLInputElement | HTMLTextAreaElement | null);
      
      if (!element) {
        if (delayMs === 0) {
          console.warn(`[FOCUS DEBUG] Attempt ${delayMs}ms: Element not found`);
          console.log(`[FOCUS DEBUG] Available IDs in DOM:`, 
            Array.from(document.querySelectorAll('[id]')).map((el) => el.id)
          );
        }
        return;
      }

      try {
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: Found element:`, element.tagName, element.id);
        
        // Check element visibility
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: Element visible:`, {
          display: style.display,
          visibility: style.visibility,
          opacity: style.opacity,
          position: rect.y,
          scrollY: window.scrollY
        });
        
        // Scroll into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: scrollIntoView executed`);
        
        // Focus
        element.focus();
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: focus() executed`);
        
        // Check if focus was actually set
        const isFocused = document.activeElement === element;
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: Element is now focused:`, isFocused);
        
        // For inputs, also select the text
        if (element instanceof HTMLInputElement) {
          element.select();
          console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: select() executed`);
        } else if (element instanceof HTMLTextAreaElement) {
          element.select();
          console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: textarea select() executed`);
        }
        
        // Dispatch focus event
        element.dispatchEvent(new Event('focus', { bubbles: true }));
        console.log(`[FOCUS DEBUG] Attempt ${delayMs}ms: Focus event dispatched`);
        
      } catch (error) {
        console.error(`[FOCUS DEBUG] Attempt ${delayMs}ms: Error:`, error);
      }
    }, delayMs);
  }

  private focusFirstInvalidField(): void {
    console.log('[FOCUS DEBUG] focusFirstInvalidField called');
    const invalidField = [
      { hasError: this.consumedMonthsError, id: 'months' },
      { hasError: this.consumedVisitsError, id: 'visits' },
      { hasError: this.consumedMileageError, id: 'mileage' },
      { hasError: this.finalResidualValueError, id: 'residualValue' },
      { hasError: this.additionalObservationsError, id: 'observationsProcess' },
      { hasError: this.dealerEmailError, id: 'dealerEmailProcess' },
      { hasError: this.sellerEmailError, id: 'sellerEmailProcess' },
      { hasError: this.ipcaPercentageError, id: 'ipcaPercentageProcess' },
      { hasError: this.administrativeTaxError, id: 'admTaxProcess' }
    ].find((field) => !!field.hasError);

    console.log('[FOCUS DEBUG] Invalid field found:', invalidField);

    if (invalidField) {
      this.focusFieldById(invalidField.id);
    } else {
      console.warn('[FOCUS DEBUG] No invalid field found');
    }
  }

  private focusFieldForInvalidReturn(): void {
    // For invalid return, residual final value is usually the main field to adjust first.
    this.focusFieldById('residualValue');
  }

  confirmCancellation(): void {
    console.log('[CONFIRM DEBUG] confirmCancellation called');
    this.formSubmitted = true;
    this.validateAllFields();
    console.log('[CONFIRM DEBUG] All fields validated');
    console.log('[CONFIRM DEBUG] isInvalidReturn:', this.calculatedValues.isInvalidReturn);
    console.log('[CONFIRM DEBUG] isFormValid:', this.isFormValid());

    if (this.calculatedValues.isInvalidReturn) {
      console.log('[CONFIRM DEBUG] Invalid return detected - focusing residual value field');
      const currentLocale = this.translateService.currentLang || 'pt-BR';
      const customerReturnFormatted = this.currencyPipe.transform(
        this.calculatedValues.customerReturn,
        'BRL',
        'symbol',
        '1.2-2',
        currentLocale
      ) || this.calculatedValues.customerReturn.toString();

      const finalPriceFormatted = this.currencyPipe.transform(
        this.contractDetails.finalPrice,
        'BRL',
        'symbol',
        '1.2-2',
        currentLocale
      ) || this.contractDetails.finalPrice.toString();

      const invalidReturnDetail = `${this.translateService.instant('TRANSLATE.CUSTOMER.RETURN.EXCEEDS')} (${customerReturnFormatted}) ${this.translateService.instant('TRANSLATE.CUSTOMER.RETURN.EXCEEDS.DESCRIPTION')} (${finalPriceFormatted}).`;

      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('TRANSLATE.ERROR'),
        detail: invalidReturnDetail
      });

      this.focusFieldForInvalidReturn();
      return;
    }

    if (this.isFormValid()) {
      console.log('[CONFIRM DEBUG] Form is valid - showing success message');
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('TRANSLATE.SUCCESS'),
        detail: this.translateService.instant('TRANSLATE.CANCELLATION.PROCESSED.SUCCESS')
      });

      console.log('Cancelamento confirmado:', this.cancellationForm);
      // Simula processamento concluido e retorna para a listagem.
      setTimeout(() => {
        this.goBack();
      }, 800);
    } else {
      console.log('[CONFIRM DEBUG] Form is invalid - focusing first invalid field');
      console.log('[CONFIRM DEBUG] Errors:', {
        consumedMonthsError: this.consumedMonthsError,
        consumedVisitsError: this.consumedVisitsError,
        consumedMileageError: this.consumedMileageError,
        finalResidualValueError: this.finalResidualValueError,
        additionalObservationsError: this.additionalObservationsError,
        dealerEmailError: this.dealerEmailError,
        sellerEmailError: this.sellerEmailError,
        ipcaPercentageError: this.ipcaPercentageError,
        administrativeTaxError: this.administrativeTaxError
      });
      
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('TRANSLATE.WARNING'),
        detail: this.translateService.instant('TRANSLATE.FORM.INVALID.CHECK.FIELDS')
      });

      this.focusFirstInvalidField();
      console.log('Formulário inválido. Por favor, corrija os erros.');
    }
  }

  private validateAllFields(): void {
    this.validateConsumedMonths();
    this.validateConsumedVisits();
    this.validateConsumedMileage();
    this.validateFinalResidualValue();
    this.validateAdditionalObservations();
    this.validateDealerEmail();
    this.validateSellerEmail();
    this.validateIpcaPercentage();
    this.validateAdministrativeTax();
  }

}
